import { Plus } from "lucide-react"
import { useTranslation } from "react-i18next"
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import { SortableContext, arrayMove, horizontalListSortingStrategy, sortableKeyboardCoordinates } from "@dnd-kit/sortable"
import { Button, Popover, PopoverContent, PopoverTrigger } from "@nexus/ui"
import { useKpiSelectorCatalog } from "@/features/kpi-settings/hooks/useKpiSelectorCatalog"
import { MIN_MY_BRAND_KPIS, useDrilldownFiltersStore } from "../stores/drilldownFilters.store"
import { MyBrandKpiPill } from "./MyBrandKpiPill"

/** My Brand's KPI order-by row: replaces the competitors-only "Order by" dropdown.
 *  Each pill both picks a visible list-view column and can drive the active sort;
 *  drag-reorders the pills (and matching table columns); "+" adds another KPI. */
export function MyBrandKpiPillRow() {
  const { t } = useTranslation()
  // Options come from the caller's own KPI settings; `myBrandById` is org-union-user
  // with user overrides winning so a user-created KPI still resolves for the pill and
  // the grid cells.
  const { myBrandCatalog, myBrandById } = useKpiSelectorCatalog("drilldown")
  const myBrandKpis = useDrilldownFiltersStore((s) => s.myBrandKpis)
  const myBrandSortMode = useDrilldownFiltersStore((s) => s.myBrandSortMode)
  const myBrandSortDirection = useDrilldownFiltersStore((s) => s.myBrandSortDirection)
  const setMyBrandKpis = useDrilldownFiltersStore((s) => s.setMyBrandKpis)
  const addMyBrandKpi = useDrilldownFiltersStore((s) => s.addMyBrandKpi)
  const removeMyBrandKpi = useDrilldownFiltersStore((s) => s.removeMyBrandKpi)
  const replaceMyBrandKpi = useDrilldownFiltersStore((s) => s.replaceMyBrandKpi)
  const toggleMyBrandKpiSort = useDrilldownFiltersStore((s) => s.toggleMyBrandKpiSort)

  // A click that doesn't move the pointer at least 5px is a click, not a drag —
  // without this, PointerSensor can swallow clicks on the name-dropdown/arrow/X.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const available = myBrandCatalog.filter((e) => !myBrandKpis.includes(e.id))
  const canRemove = myBrandKpis.length > MIN_MY_BRAND_KPIS

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = myBrandKpis.indexOf(active.id as string)
    const newIndex = myBrandKpis.indexOf(over.id as string)
    if (oldIndex === -1 || newIndex === -1) return
    setMyBrandKpis(arrayMove(myBrandKpis, oldIndex, newIndex))
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={myBrandKpis} strategy={horizontalListSortingStrategy}>
          {myBrandKpis.map((id) => {
            const entry = myBrandById[id]
            if (!entry) return null
            return (
              <MyBrandKpiPill
                key={id}
                id={id}
                entry={entry}
                catalog={myBrandCatalog}
                isActiveSort={myBrandSortMode === id}
                sortDirection={myBrandSortDirection}
                canRemove={canRemove}
                selectedIds={myBrandKpis}
                onToggleSort={toggleMyBrandKpiSort}
                onRemove={removeMyBrandKpi}
                onReplace={replaceMyBrandKpi}
              />
            )
          })}
        </SortableContext>
      </DndContext>

      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={t("drilldown.myBrandKpi.addKpi", "Add KPI")}
            data-testid="add-my-brand-kpi"
            disabled={available.length === 0}
            className="flex size-[34px] items-center justify-center rounded-[10px] border border-dashed border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Plus className="size-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="max-h-[320px] w-[220px] overflow-y-auto p-1">
          {available.map((e) => (
            <Button
              key={e.id}
              variant="ghost"
              size="sm"
              data-testid={`add-my-brand-kpi-option-${e.id}`}
              className="w-full justify-start gap-2 font-normal"
              onClick={() => addMyBrandKpi(e.id)}
            >
              <span className="size-3 shrink-0 rounded-[4px]" style={{ background: e.colorVar }} aria-hidden />
              {e.label}
            </Button>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  )
}
